import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Stack, TablePagination, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { Loader, StickyActionBar } from '~/shared/ui'
import {
  isTerminalJobStatus,
  useCareTeamSwapJobPoll,
  useCareTeamSwapLookups,
  useCareTeamSwapSearch,
  useCreateCareTeamSwapJob,
  type CareTeamSwapSearchBody,
} from './CareTeamSwapService'
import { deriveRoleGroups, type ProviderOption } from './roles'
import ReassignmentStepper from './ReassignmentStepper'
import CurrentTeamSection, { type CohortFilters } from './CurrentTeamSection'
import NewTeamSection from './NewTeamSection'
import ResultsTable from './ResultsTable'
import LicensureLegend from './LicensureLegend'
import ReviewSection from './ReviewSection'
import ConfirmSwapModal, { type SwapDiffRow } from './ConfirmSwapModal'

type EnrollmentStatus = CareTeamSwapSearchBody['primary_care_enrollment_status']

const EMPTY_FILTERS: CohortFilters = { state: '', contract: '', enrollmentStatus: '' }

// Page size for the server-paginated cohort. Matches the backend's default limit.
const PAGE_SIZE = 100

/**
 * Operator screen for reassigning a cohort of members from one care-team provider to another.
 * Ops selects the departing provider per role plus optional cohort filters, searches, proposes a
 * replacement per role, validates the proposed licensure, and submits an async reassignment job it
 * polls to a terminal state. All cohort and licensure logic is computed server-side and reaches the
 * screen through the care-team-swap BFF endpoints.
 */
const CareTeamReassignmentSettings = () => {
  const { data: lookups, isPending: lookupsLoading } = useCareTeamSwapLookups()

  const [currentByRole, setCurrentByRole] = useState<Record<string, string>>({})
  const [newByRole, setNewByRole] = useState<Record<string, string>>({})
  const [checkByRole, setCheckByRole] = useState<Record<string, boolean>>({})
  const [filters, setFilters] = useState<CohortFilters>(EMPTY_FILTERS)
  const [searchBody, setSearchBody] = useState<CareTeamSwapSearchBody | null>(null)
  const [page, setPage] = useState(0)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [jobId, setJobId] = useState<number | null>(null)
  // Completed sections auto-collapse to a summary to free vertical space; each stays re-expandable.
  const [currentCollapsed, setCurrentCollapsed] = useState(false)
  const [newCollapsed, setNewCollapsed] = useState(false)

  const { data: searchResults, isFetching: isSearching } = useCareTeamSwapSearch(searchBody, {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })
  const createJob = useCreateCareTeamSwapJob()
  const { data: job } = useCareTeamSwapJobPoll(jobId)

  // A search that carries proposed swaps is a validated run: its rows include the resulting
  // (post-swap) care team and its licensure.
  const hasSwaps = (searchBody?.swaps?.length ?? 0) > 0
  const validated = hasSwaps && !!searchResults

  const roles = useMemo(() => (lookups ? deriveRoleGroups(lookups) : []), [lookups])
  const providerById = useMemo(() => {
    const map = new Map<number, ProviderOption>()
    lookups?.providers.forEach(provider => map.set(provider.id, provider))
    return map
  }, [lookups])

  const currentProviderIds = Object.values(currentByRole).filter(Boolean).map(Number)

  // A swap exists for a role only when both a departing and a replacement provider are chosen.
  const proposedSwaps = useMemo(
    () =>
      roles.flatMap(role => {
        const existing = currentByRole[role.name]
        const replacement = newByRole[role.name]
        if (!existing || !replacement || existing === replacement) return []
        return [
          {
            role,
            existing: Number(existing),
            replacement: Number(replacement),
            skipLicenseCheck: role.clinical ? !(checkByRole[role.name] ?? true) : true,
          },
        ]
      }),
    [roles, currentByRole, newByRole, checkByRole],
  )

  const filterBody = () => ({
    state: filters.state ? Number(filters.state) : null,
    contract: filters.contract ? Number(filters.contract) : null,
    primary_care_enrollment_status: (filters.enrollmentStatus || null) as EnrollmentStatus,
  })

  const swapsBody = proposedSwaps.map(swap => ({
    existing_provider: swap.existing,
    new_provider: swap.replacement,
    skip_license_check: swap.skipLicenseCheck,
  }))

  // Drop any proposed swaps from the running search: rerun as a plain cohort search (hiding the
  // proposed columns and the apply action) so the operator must Validate licensure again after
  // changing a replacement or a filter.
  const clearValidation = () => {
    setSearchBody(prev => (prev ? { ...prev, swaps: [] } : prev))
    setPage(0)
  }

  const handleCurrentChange = (roleName: string, value: string) => {
    setCurrentByRole(prev => ({ ...prev, [roleName]: value }))
    // Only a role with a departing provider is swappable. If one is cleared, drop any replacement
    // picked for it so a stale value can't linger behind the now-disabled new-team field.
    if (!value) setNewByRole(prev => ({ ...prev, [roleName]: '' }))
    // Changing a departing provider changes the swap pairing (and cohort), so invalidate the current
    // validation like the other edit handlers — otherwise the stale validated results, apply bar, and
    // tallies stay up while swapsBody recomputes live, letting Apply submit a never-validated swap.
    clearValidation()
  }
  const handleNewChange = (roleName: string, value: string) => {
    setNewByRole(prev => ({ ...prev, [roleName]: value }))
    clearValidation()
  }
  const handleCheckChange = (roleName: string, checked: boolean) => {
    setCheckByRole(prev => ({ ...prev, [roleName]: checked }))
    clearValidation()
  }
  const handleFilterChange = (patch: Partial<CohortFilters>) => {
    setFilters(prev => ({ ...prev, ...patch }))
    clearValidation()
  }
  const handleSearch = () => {
    // A new search invalidates any prior validation: drop the swaps (which hides the results'
    // proposed columns and the Update care teams action) and re-expand the new-team section so the
    // operator must run Validate licensure again before applying.
    setSearchBody({ current_provider_ids: currentProviderIds, ...filterBody() })
    setPage(0)
    setCurrentCollapsed(true)
    setNewCollapsed(false)
  }
  const handleValidate = () => {
    // Re-run the same cohort search with the proposed swaps attached: each row then carries its
    // resulting-team licensure, so no separate validate call is needed.
    setSearchBody(prev => (prev ? { ...prev, swaps: swapsBody } : prev))
    setPage(0)
    setNewCollapsed(true)
  }
  const handleReset = () => {
    setCurrentByRole({})
    setNewByRole({})
    setCheckByRole({})
    setFilters(EMPTY_FILTERS)
    setSearchBody(null)
    setPage(0)
    setJobId(null)
    setCurrentCollapsed(false)
    setNewCollapsed(false)
  }
  const handleConfirm = () =>
    createJob.mutate(
      { swaps: swapsBody, ...filterBody() },
      { onSuccess: created => setJobId(created.id) },
    )
  const handleCloseConfirm = () => {
    setConfirmOpen(false)
    // After a successful reassignment the cohort no longer matches the departing providers, so the
    // search would refetch to 0 results. Reset the whole page back to step 1 instead. A cancel or a
    // failed job leaves the selections in place so the operator can adjust and retry.
    if (job?.status === 'complete' || job?.status === 'skipped') {
      handleReset()
    } else {
      setJobId(null)
    }
  }

  // departing provider id -> replacement full name, for the current -> proposed table chips
  const replacementByProvider = useMemo(() => {
    const map = new Map<number, string>()
    proposedSwaps.forEach(swap =>
      map.set(swap.existing, providerById.get(swap.replacement)?.full_name ?? ''),
    )
    return map
  }, [proposedSwaps, providerById])

  const diff: SwapDiffRow[] = proposedSwaps.map(swap => ({
    abbr: swap.role.abbr,
    oldName: providerById.get(swap.existing)?.full_name ?? '',
    newName: providerById.get(swap.replacement)?.full_name ?? '',
  }))

  // Bring the results into view after a search or a validation (search-with-swaps) completes.
  const resultsRef = useRef<HTMLDivElement>(null)
  const scrollToResults = () =>
    resultsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  useEffect(() => {
    if (searchResults) scrollToResults()
  }, [searchResults])

  if (lookupsLoading || !lookups) return <Loader />

  // A search carrying swaps is a validate run; a plain search is not. Split the in-flight flag so
  // each section spins its own action button.
  const isValidating = isSearching && hasSwaps
  const isPlainSearching = isSearching && !hasSwaps
  const activeStep = !searchBody ? 0 : validated ? 2 : 1
  const memberCount = searchResults?.count ?? 0

  return (
    <Box sx={{ p: 3, width: '100%' }} data-testid="care-team-reassignment-settings">
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Care team reassignment
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>
        Swap a departing provider for a replacement across their whole panel, with a before/after
        state-licensure check. The system surfaces license issues but never blocks — you decide
        whether to proceed.
      </Typography>

      <ReassignmentStepper activeStep={activeStep} />

      <Stack spacing={2.25}>
        <CurrentTeamSection
          lookups={lookups}
          roles={roles}
          currentByRole={currentByRole}
          onCurrentChange={handleCurrentChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onReset={handleReset}
          canSearch={currentProviderIds.length > 0}
          isSearching={isPlainSearching}
          collapsed={currentCollapsed}
          onToggleCollapsed={() => setCurrentCollapsed(prev => !prev)}
          resultCount={searchResults ? memberCount : null}
        />

        {!searchBody && (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              color: 'text.secondary',
            }}
            data-testid="pre-search-guidance"
          >
            <Typography sx={{ fontSize: 14 }}>
              Select one or more departing providers above and search to find the members
              you&apos;ll reassign.
            </Typography>
          </Box>
        )}

        {searchBody && (
          <NewTeamSection
            roles={roles}
            currentByRole={currentByRole}
            newByRole={newByRole}
            onNewChange={handleNewChange}
            checkByRole={checkByRole}
            onCheckChange={handleCheckChange}
            onValidate={handleValidate}
            canValidate={proposedSwaps.length > 0}
            isValidating={isValidating}
            locked={false}
            collapsed={newCollapsed}
            onToggleCollapsed={() => setNewCollapsed(prev => !prev)}
          />
        )}

        {searchBody &&
          (isSearching ? (
            <Loader />
          ) : searchResults ? (
            <Box ref={resultsRef}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1.25,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                  {memberCount.toLocaleString()} members
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                  paneled with the selected team
                </Typography>
              </Box>
              {searchResults.results.length === 0 ? (
                <Typography sx={{ color: 'text.secondary' }} data-testid="no-results">
                  No members match the selected current providers and filters.
                </Typography>
              ) : (
                <>
                  <ResultsTable
                    members={searchResults.results}
                    validated={validated}
                    replacementByProvider={replacementByProvider}
                  />
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <LicensureLegend />
                    <TablePagination
                      component="div"
                      count={memberCount}
                      page={page}
                      rowsPerPage={PAGE_SIZE}
                      rowsPerPageOptions={[PAGE_SIZE]}
                      onPageChange={(_, nextPage) => setPage(nextPage)}
                      data-testid="results-pagination"
                    />
                  </Box>
                </>
              )}
            </Box>
          ) : null)}

        {validated && <ReviewSection locked={false} />}
      </Stack>

      {validated && (
        <StickyActionBar
          testId="sticky-apply-bar"
          status={
            <Typography sx={{ fontSize: 14 }}>
              <b>{memberCount.toLocaleString()}</b> members
            </Typography>
          }
        >
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => setConfirmOpen(true)}
            data-testid="apply-button"
          >
            Update care teams
          </Button>
        </StickyActionBar>
      )}

      <ConfirmSwapModal
        open={confirmOpen}
        onClose={handleCloseConfirm}
        diff={diff}
        memberCount={memberCount}
        onConfirm={handleConfirm}
        status={job?.status ?? null}
        isSubmitting={createJob.isPending || (jobId != null && !isTerminalJobStatus(job?.status))}
      />
    </Box>
  )
}

export default CareTeamReassignmentSettings
